import React, { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../../assets/images/logo.png";
import { GlobalContext } from "../../../context/Provider";
import {
  ROLES_BACKUP,
  ROLES_CONTACT_US,
  ROLES_TRANSACTION,
  ROLES_VEHICLE,
  ROLES_BOOKING,
  ROLES_DASHBOARD,
  ROLES_FAQS,
  ROLES_RIDES,
  ROLES_NOTIFICATION,
  ROLES_POLICIES,
  ROLES_USERS,
  ROLES_COUPONS,
  ROLES_TRANSFER,
  ROLES_SAVED,
  ROLES_SETTINGS,
  ROLES_EARNINGS,
  ROLES_RIDEREPORT,
} from "../../../constants/actionTypes";
import {
  LayoutDashboard,
  Users,
  CalendarCheck2,
  Compass,
  Bookmark,
  Car,
  Receipt,
  ArrowLeftRight,
  TrendingUp,
  TicketPercent,
  ShieldAlert,
  AlertTriangle,
  HelpCircle,
  FileText,
  MessageSquare,
  Bell,
  Settings,
  Database,
  X,
} from "lucide-react";

interface SidebarProps {
  isVisible: boolean;
  toggle: () => void;
  logOutNow?: () => void;
}

interface MenuItemType {
  name: string;
  path: string;
  matchpath: string;
  icon: any;
  role: string;
  exact?: boolean;
  pending_count?: number;
}

interface ModuleType {
  title: string;
  items: MenuItemType[];
}

export default (props: SidebarProps) => {
  const { authState, staticDataState } = useContext(GlobalContext);
  const roles = Array.isArray(authState.roles) ? authState.roles : [];
  const location = useLocation();

  useEffect(() => {
    if (window.innerWidth <= 768 && props.isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [props.isVisible]);

  const modules: ModuleType[] = [
    {
      title: "Main",
      items: [
        {
          name: "Dashboard",
          path: "/",
          matchpath: "/",
          exact: true,
          icon: LayoutDashboard,
          role: ROLES_DASHBOARD,
        },
        {
          name: "Users",
          path: "/users/1",
          matchpath: "/users",
          icon: Users,
          role: ROLES_USERS,
        },
      ],
    },
    {
      title: "Booking Section",
      items: [
        {
          name: "Booked Rides",
          path: "/booked-rides/1",
          matchpath: "/booked-rides",
          icon: CalendarCheck2,
          role: ROLES_BOOKING,
        },
        {
          name: "Created Rides",
          path: "/created-rides/1",
          matchpath: "/created-rides",
          icon: Compass,
          role: ROLES_RIDES,
        },
        {
          name: "Saved Rides",
          path: "/saved-rides/1",
          matchpath: "/saved-rides",
          icon: Bookmark,
          role: ROLES_SAVED,
        },
        {
          name: "Vehicles",
          path: "/vehicles/1",
          matchpath: "/vehicle",
          icon: Car,
          role: ROLES_VEHICLE,
        },
      ],
    },
    {
      title: "Finance",
      items: [
        {
          name: "Transactions",
          path: "/transactions/1",
          matchpath: "/transactions",
          icon: Receipt,
          role: ROLES_TRANSACTION,
        },
        {
          name: "Transfer",
          path: "/transfer/1",
          matchpath: "/transfer",
          icon: ArrowLeftRight,
          role: ROLES_TRANSFER,
        },
        {
          name: "Earnings & Tax",
          path: "/earnings-and-tax/1",
          matchpath: "/earnings-and-tax",
          icon: TrendingUp,
          role: ROLES_EARNINGS,
        },
        {
          name: "Coupons",
          path: "/coupons/1",
          matchpath: "/coupons",
          icon: TicketPercent,
          role: ROLES_COUPONS,
        },
      ],
    },
    {
      title: "Content & Queries",
      items: [
        {
          name: "Contact Us",
          path: "/contact-us/1",
          matchpath: "/contact-us",
          icon: MessageSquare,
          role: ROLES_CONTACT_US,
          pending_count: staticDataState?.dashboard?.total_pending_contact_us,
        },
        {
          name: "Reported Rides",
          path: "/reported-rides/1",
          matchpath: "/reported-rides",
          icon: AlertTriangle,
          role: ROLES_RIDEREPORT,
        },
        {
          name: "Reported Users",
          path: "/report-user/1",
          matchpath: "/report-user",
          icon: ShieldAlert,
          role: ROLES_RIDEREPORT,
        },
        {
          name: "FAQs",
          path: "/FAQs/1".toLocaleLowerCase(),
          matchpath: "/FAQs",
          icon: HelpCircle,
          role: ROLES_FAQS,
        },
        {
          name: "Contents",
          path: "/contents",
          matchpath: "/contents",
          icon: FileText,
          role: ROLES_POLICIES,
        },
      ],
    },
    {
      title: "Settings & System",
      items: [
        {
          name: "Notifications",
          path: "/notifications",
          matchpath: "/notifications",
          icon: Bell,
          role: ROLES_NOTIFICATION,
        },
        {
          name: "Settings",
          path: "/settings",
          matchpath: "/settings",
          icon: Settings,
          role: ROLES_SETTINGS,
        },
        {
          name: "DB Backup",
          path: "/db-backup",
          matchpath: "/db-backup",
          icon: Database,
          role: ROLES_BACKUP,
        },
      ],
    },
  ];

  const handleLinkClick = () => {
    if (window.innerWidth <= 768 && props.isVisible && typeof props.toggle === "function") {
      props.toggle();
    }
  };

  const hasAccess = (role: string) => {
    return Boolean(roles.includes(role) || authState.super_admin);
  };

  const isItemActive = (item: any) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return (
      item.matchpath &&
      location.pathname.toLowerCase().startsWith(item.matchpath.toLowerCase())
    );
  };

  return (
    <>
      {props.isVisible && (
        <div
          className="sidebar-backdrop d-md-none"
          onClick={props.toggle}
          aria-hidden="true"
        />
      )}
      <nav
        className={`navbar-default navbar-static-side ${props.isVisible === true ? "open-nav" : ""}`}
        role="navigation"
      >
        <div className="sidebar-collapse">
          <div
            className="arrow d-md-none"
            onClick={props.toggle}
            role="button"
            aria-label="Close sidebar"
          >
            <X size={18} />
          </div>
          <div className="m-6">
            <Link to="/" className="text-decoration-none">
              <h1 className="text-3xl text-center font-bold text-black m-0">
                <span className="text-primary">Ready</span>Share
              </h1>
            </Link>
          </div>
          <ul className="nav" id="side-menu">
            {modules.map((mod, modIdx) => {
              const visibleItems = mod.items.filter((item) => hasAccess(item.role));
              if (visibleItems.length === 0) return null;

              return (
                <React.Fragment key={mod.title || modIdx}>
                  <li className={`nav-section-title ${modIdx > 0 ? "has-divider" : ""}`}>
                    <span>{mod.title}</span>
                  </li>
                  {visibleItems.map((res: any, index: number) => {
                    const Icon = res.icon;
                    const isActive = isItemActive(res);

                    return (
                      <li key={res.path || index} className={isActive ? "active" : ""}>
                        <Link to={res.path} onClick={handleLinkClick}>
                          <div className="justify-content-between d-flex align-items-center">
                            <div className="d-flex align-items-center">
                              <Icon className="sidebar-nav-icon me-2.5 shrink-0" size={18} />
                              <span className="nav-label">{res.name}</span>
                            </div>
                            {typeof res.pending_count === "number" && res.pending_count > 0 ? (
                              <div
                                className={`fw-bold text-xs px-2 py-0.5 rounded-full ${isActive
                                  ? "bg-white/20 text-white"
                                  : "bg-emerald-50 text-[#006747] border border-emerald-200/60"
                                  }`}
                              >
                                {res.pending_count}
                              </div>
                            ) : null}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      </nav>
    </>
  );
};